import React, { useState, useEffect, useMemo } from 'react';
import * as apiClient from '../services/apiClient';
import { 
  ArrowLeft, 
  CreditCard, 
  Send, 
  Heart, 
  MessageSquare, 
  ShieldCheck, 
  AlertTriangle, 
  Trash2, 
  Flag, 
  Sparkles, 
  Clock, 
  Flame, 
  User, 
  Check, 
  X 
} from 'lucide-react';

/**
 * Check if text contains PAN (card numbers) or CVV patterns
 */
function containsPaymentCardInfo(text) {
  if (!text || typeof text !== 'string') return false;
  const panRegex = /\b(?:\d[ -]*?){13,19}\b/;
  const cvvRegex = /\b(?:cvv|cvc|security\s*code|exp|expiry)[\s:=#]*\d{3,4}\b/i;
  const strictCvvRegex = /\b\d{3,4}\s*(?:cvv|cvc)\b/i;
  return panRegex.test(text) || cvvRegex.test(text) || strictCvvRegex.test(text);
}

export default function CardCommunityView({
  masterCardId,
  cardData,
  onBack,
  showToast = () => {}
}) {
  const [card, setCard] = useState(cardData || null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('recent'); // 'recent' | 'popular'

  // Post composer
  const [postContent, setPostContent] = useState('');
  const [isSubmittingPost, setIsSubmittingPost] = useState(false);

  // Active comments thread
  const [activePostId, setActivePostId] = useState(null);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentContent, setCommentContent] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  // Reporting modal
  const [reportTarget, setReportTarget] = useState(null); // { type: 'POST' | 'COMMENT', id: string }
  const [reportReason, setReportReason] = useState('Spam or irrelevant');
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);

  // Load card details & posts
  useEffect(() => {
    if (!masterCardId) return;
    loadCardAndPosts();
  }, [masterCardId, sortBy]);

  async function loadCardAndPosts() {
    setLoading(true);
    try {
      const [cardRes, postsRes] = await Promise.all([
        apiClient.request(`/catalog/cards/${masterCardId}`),
        apiClient.request(`/catalog/cards/${masterCardId}/posts?sort=${sortBy}`)
      ]);
      setCard(cardRes);
      setPosts(Array.isArray(postsRes) ? postsRes : []);
    } catch (err) {
      console.error('Failed to load card community:', err);
      showToast(err.message || 'Failed to load community feed', 'error');
    } finally {
      setLoading(false);
    }
  }

  // Load comments for a post
  async function loadComments(postId) {
    setActivePostId(postId);
    setLoadingComments(true);
    try {
      const res = await apiClient.request(`/catalog/posts/${postId}/comments`);
      setComments(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error('Failed to load comments:', err);
      showToast('Failed to load comments', 'error');
    } finally {
      setLoadingComments(false);
    }
  }

  // Handle post submit
  async function handleCreatePost(e) {
    e.preventDefault();
    if (!postContent.trim()) return;

    if (containsPaymentCardInfo(postContent)) {
      showToast('Sensitive payment card information detected (card numbers / CVVs are strictly prohibited).', 'error');
      return;
    }

    setIsSubmittingPost(true);
    try {
      const newPost = await apiClient.request(`/catalog/cards/${masterCardId}/posts`, 'POST', {
        content: postContent.trim()
      });
      setPosts(prev => [newPost, ...prev]);
      setPostContent('');
      showToast('Post shared with community!', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to create post', 'error');
    } finally {
      setIsSubmittingPost(false);
    }
  }

  // Handle comment submit
  async function handleCreateComment(e) {
    e.preventDefault();
    if (!commentContent.trim() || !activePostId) return;

    if (containsPaymentCardInfo(commentContent)) {
      showToast('Sensitive payment card information detected (card numbers / CVVs are strictly prohibited).', 'error');
      return;
    }

    setIsSubmittingComment(true);
    try {
      const newComment = await apiClient.request(`/catalog/posts/${activePostId}/comments`, 'POST', {
        content: commentContent.trim()
      });
      setComments(prev => [...prev, newComment]);
      setCommentContent('');
      // Update comment count on post in feed
      setPosts(prev => prev.map(p => p.post_id === activePostId ? { ...p, comments_count: (p.comments_count || 0) + 1 } : p));
      showToast('Comment posted!', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to post comment', 'error');
    } finally {
      setIsSubmittingComment(false);
    }
  }

  // Handle post like
  async function handleToggleLikePost(postId) {
    try {
      const res = await apiClient.request(`/catalog/posts/${postId}/like`, 'POST', {});
      setPosts(prev => prev.map(p => {
        if (p.post_id === postId) {
          return {
            ...p,
            user_has_liked: res.liked,
            likes_count: res.likes_count
          };
        }
        return p;
      }));
    } catch (err) {
      showToast(err.message || 'Failed to update like', 'error');
    }
  }

  // Handle comment like
  async function handleToggleLikeComment(commentId) {
    try {
      const res = await apiClient.request(`/catalog/comments/${commentId}/like`, 'POST', {});
      setComments(prev => prev.map(c => {
        if (c.comment_id === commentId) {
          return {
            ...c,
            user_has_liked: res.liked,
            likes_count: res.likes_count
          };
        }
        return c;
      }));
    } catch (err) {
      showToast(err.message || 'Failed to update like', 'error');
    }
  }

  // Handle report submit
  async function handleSendReport(e) {
    e.preventDefault();
    if (!reportTarget) return;

    setIsSubmittingReport(true);
    try {
      await apiClient.request('/catalog/report', 'POST', {
        entity_type: reportTarget.type,
        entity_id: reportTarget.id,
        reason: reportReason
      });
      showToast('Content reported for moderation review. Thank you!', 'success');
      setReportTarget(null);
    } catch (err) {
      showToast(err.message || 'Failed to submit report', 'error');
    } finally {
      setIsSubmittingReport(false);
    }
  }

  // Handle post delete
  async function handleDeletePost(postId) {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await apiClient.request(`/catalog/posts/${postId}`, 'DELETE');
      setPosts(prev => prev.filter(p => p.post_id !== postId));
      showToast('Post deleted', 'success');
      if (activePostId === postId) setActivePostId(null);
    } catch (err) {
      showToast(err.message || 'Failed to delete post', 'error');
    }
  }

  // Handle comment delete
  async function handleDeleteComment(commentId) {
    try {
      await apiClient.request(`/catalog/comments/${commentId}`, 'DELETE');
      setComments(prev => prev.filter(c => c.comment_id !== commentId));
      setPosts(prev => prev.map(p => p.post_id === activePostId ? { ...p, comments_count: Math.max(0, (p.comments_count || 1) - 1) } : p));
      showToast('Comment deleted', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to delete comment', 'error');
    }
  }

  const hasPaymentInfoInPost = useMemo(() => containsPaymentCardInfo(postContent), [postContent]);
  const hasPaymentInfoInComment = useMemo(() => containsPaymentCardInfo(commentContent), [commentContent]);

  return (
    <div 
      className="animate-fade-in"
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        background: 'var(--bg-main, #0b0c10)',
        color: 'var(--text-main)',
        padding: '0 0 80px 0'
      }}
    >
      {/* Top Sticky Navigation Bar */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        background: 'rgba(15, 17, 26, 0.92)',
        backdropFilter: 'blur(16px)',
        borderBottom: '1px solid var(--border-glass)',
        padding: '12px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            type="button"
            onClick={onBack}
            style={{
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid var(--border-glass)',
              borderRadius: '10px',
              padding: '8px 12px',
              color: 'var(--accent-cyan)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              fontWeight: '700'
            }}
          >
            <ArrowLeft size={16} /> Back
          </button>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h1 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: 'var(--text-main)' }}>
                {card?.card_name || 'Card Community'}
              </h1>
              {card?.card_tier && (
                <span style={{ fontSize: '10px', background: 'rgba(165,94,234,0.2)', color: 'var(--accent-purple)', padding: '2px 6px', borderRadius: '4px', fontWeight: '700' }}>
                  {card.card_tier}
                </span>
              )}
            </div>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              {card?.bank_name} • Cardholder Community Feed
            </span>
          </div>
        </div>

        {/* User Cardholder Status Badge */}
        {card?.is_cardholder && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'rgba(0, 245, 212, 0.12)',
            border: '1px solid rgba(0, 245, 212, 0.3)',
            padding: '4px 10px',
            borderRadius: '20px',
            fontSize: '11px',
            color: 'var(--accent-cyan)',
            fontWeight: '700'
          }}>
            <ShieldCheck size={14} />
            <span>Verified Cardholder</span>
          </div>
        )}
      </div>

      <div style={{ maxWidth: '780px', width: '100%', margin: '0 auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px', boxSizing: 'border-box' }}>
        {/* Card Header Card */}
        <div className="glass-panel" style={{
          padding: '18px 20px',
          borderRadius: '16px',
          background: 'linear-gradient(135deg, rgba(30, 32, 54, 0.7), rgba(18, 20, 36, 0.85))',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{
              width: '46px',
              height: '46px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, rgba(165,94,234,0.3), rgba(84,160,255,0.3))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--accent-purple)'
            }}>
              <CreditCard size={24} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: 'var(--text-main)' }}>
                {card?.card_name}
              </h2>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                {card?.bank_name} • Fee: {card?.annual_fee === 0 ? 'Free for Life' : `AED ${card?.annual_fee}`}
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontSize: '16px', fontWeight: '800', color: 'var(--accent-cyan)', display: 'block' }}>
                {card?.cardholders_count || 0}
              </span>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Tezer Cardholders</span>
            </div>
          </div>
        </div>

        {/* Create Post Composer */}
        <div className="glass-panel" style={{
          padding: '16px 20px',
          borderRadius: '16px',
          border: '1px solid var(--border-glass)',
          background: 'rgba(255, 255, 255, 0.02)',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <MessageSquare size={16} color="var(--accent-purple)" />
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700' }}>
              Share with {card?.card_name} Cardholders
            </h3>
          </div>

          <form onSubmit={handleCreatePost} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <textarea
              placeholder="Ask about spending limits, lounge experiences, fee waivers, or offer stacking tips..."
              value={postContent}
              onChange={e => setPostContent(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '12px 14px',
                borderRadius: '12px',
                background: 'rgba(0, 0, 0, 0.25)',
                border: hasPaymentInfoInPost ? '1px solid var(--color-outflow)' : '1px solid var(--border-glass)',
                color: 'var(--text-main)',
                fontSize: '13px',
                resize: 'vertical',
                boxSizing: 'border-box',
                outline: 'none',
                lineHeight: '1.5'
              }}
            />

            {/* Privacy & Anti-PAN Guard Warning */}
            {hasPaymentInfoInPost ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                borderRadius: '8px',
                background: 'rgba(255, 71, 87, 0.15)',
                border: '1px solid rgba(255, 71, 87, 0.3)',
                color: 'var(--color-outflow)',
                fontSize: '11.5px',
                fontWeight: '600'
              }}>
                <AlertTriangle size={15} />
                <span>Sensitive payment card information detected (card numbers / CVVs are strictly prohibited).</span>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', color: 'var(--text-muted)' }}>
                <span>🔒 Privacy Protected: Your financial numbers & private nicknames are never shared.</span>
                <span>{postContent.length} / 2,000</span>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
              <button
                type="submit"
                disabled={!postContent.trim() || hasPaymentInfoInPost || isSubmittingPost}
                className="btn-primary"
                style={{
                  padding: '8px 20px',
                  borderRadius: '10px',
                  fontSize: '12.5px',
                  fontWeight: '700',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: (!postContent.trim() || hasPaymentInfoInPost || isSubmittingPost) ? 0.5 : 1
                }}
              >
                <Send size={13} /> {isSubmittingPost ? 'Posting...' : 'Post to Community'}
              </button>
            </div>
          </form>
        </div>

        {/* Feed Filter Sort Bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-muted)' }}>
            Community Discussions ({posts.length})
          </span>

          <div style={{ display: 'flex', gap: '6px', background: 'rgba(255, 255, 255, 0.04)', padding: '3px', borderRadius: '10px' }}>
            <button
              type="button"
              onClick={() => setSortBy('recent')}
              style={{
                padding: '5px 12px',
                borderRadius: '8px',
                border: 'none',
                background: sortBy === 'recent' ? 'var(--accent-purple)' : 'transparent',
                color: sortBy === 'recent' ? 'white' : 'var(--text-muted)',
                fontSize: '11px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <Clock size={12} /> Recent
            </button>
            <button
              type="button"
              onClick={() => setSortBy('popular')}
              style={{
                padding: '5px 12px',
                borderRadius: '8px',
                border: 'none',
                background: sortBy === 'popular' ? 'var(--accent-purple)' : 'transparent',
                color: sortBy === 'popular' ? 'white' : 'var(--text-muted)',
                fontSize: '11px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <Flame size={12} /> Popular
            </button>
          </div>
        </div>

        {/* Feed Posts */}
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
            <Sparkles size={24} className="animate-spin" color="var(--accent-purple)" style={{ margin: '0 auto 10px' }} />
            <p style={{ margin: 0, fontSize: '13px' }}>Loading discussions...</p>
          </div>
        ) : posts.length === 0 ? (
          <div style={{
            padding: '50px 20px',
            textAlign: 'center',
            color: 'var(--text-muted)',
            background: 'rgba(255,255,255,0.02)',
            borderRadius: '16px',
            border: '1px solid var(--border-glass)'
          }}>
            <MessageSquare size={36} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
            <h3 style={{ margin: '0 0 6px 0', fontSize: '16px', color: 'var(--text-main)' }}>No Discussions Yet</h3>
            <p style={{ margin: 0, fontSize: '12px' }}>Start the conversation above with your fellow cardholders!</p>
          </div>
        ) : (
          posts.map(post => {
            const isCommentsOpen = activePostId === post.post_id;

            return (
              <div
                key={post.post_id}
                className="glass-panel"
                style={{
                  padding: '16px 20px',
                  borderRadius: '16px',
                  border: '1px solid var(--border-glass)',
                  background: 'rgba(255, 255, 255, 0.02)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}
              >
                {/* Author Info & Badges */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '34px',
                      height: '34px',
                      borderRadius: '50%',
                      background: 'rgba(165,94,234,0.2)',
                      border: '1px solid rgba(165,94,234,0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--accent-purple)',
                      overflow: 'hidden',
                      flexShrink: 0
                    }}>
                      {post.author_avatar ? (
                        <img src={post.author_avatar} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <User size={18} />
                      )}
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-main)' }}>
                          {post.author_name}
                        </span>
                        {post.is_cardholder && (
                          <span style={{
                            fontSize: '9.5px',
                            background: 'rgba(0, 245, 212, 0.12)',
                            color: 'var(--accent-cyan)',
                            border: '1px solid rgba(0, 245, 212, 0.25)',
                            padding: '1px 5px',
                            borderRadius: '4px',
                            fontWeight: '700',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px'
                          }}>
                            <ShieldCheck size={11} /> Verified Cardholder
                          </span>
                        )}
                      </div>
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>

                  {/* Actions Dropdown / Delete or Report */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {post.is_author && (
                      <button
                        type="button"
                        onClick={() => handleDeletePost(post.post_id)}
                        style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer', padding: '4px' }}
                        title="Delete post"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setReportTarget({ type: 'POST', id: post.post_id })}
                      style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '4px' }}
                      title="Report content"
                    >
                      <Flag size={14} />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <p style={{
                  margin: 0,
                  fontSize: '13.5px',
                  color: 'var(--text-main)',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap'
                }}>
                  {post.content}
                </p>

                {/* Bottom Bar: Likes & Comments Trigger */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  paddingTop: '8px',
                  borderTop: '1px solid rgba(255, 255, 255, 0.04)',
                  fontSize: '12px'
                }}>
                  <button
                    type="button"
                    onClick={() => handleToggleLikePost(post.post_id)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: post.user_has_liked ? 'var(--color-outflow)' : 'var(--text-muted)',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      fontWeight: '700'
                    }}
                  >
                    <Heart size={15} fill={post.user_has_liked ? 'var(--color-outflow)' : 'none'} />
                    <span>{post.likes_count || 0}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (isCommentsOpen) {
                        setActivePostId(null);
                      } else {
                        loadComments(post.post_id);
                      }
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: isCommentsOpen ? 'var(--accent-cyan)' : 'var(--text-muted)',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      fontWeight: '600'
                    }}
                  >
                    <MessageSquare size={15} />
                    <span>{post.comments_count || 0} {post.comments_count === 1 ? 'comment' : 'comments'}</span>
                  </button>
                </div>

                {/* Comments Thread Section */}
                {isCommentsOpen && (
                  <div style={{
                    marginTop: '8px',
                    paddingTop: '12px',
                    borderTop: '1px solid rgba(255, 255, 255, 0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    {loadingComments ? (
                      <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '11px' }}>
                        Loading comments...
                      </div>
                    ) : comments.length === 0 ? (
                      <div style={{ padding: '8px 12px', color: 'var(--text-muted)', fontSize: '11.5px' }}>
                        No comments yet. Be the first to reply!
                      </div>
                    ) : (
                      comments.map(comment => (
                        <div
                          key={comment.comment_id}
                          style={{
                            padding: '10px 12px',
                            borderRadius: '10px',
                            background: 'rgba(0, 0, 0, 0.25)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '6px'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-main)' }}>
                                {comment.author_name}
                              </span>
                              {comment.is_cardholder && (
                                <span style={{
                                  fontSize: '9px',
                                  background: 'rgba(0, 245, 212, 0.1)',
                                  color: 'var(--accent-cyan)',
                                  padding: '1px 4px',
                                  borderRadius: '3px',
                                  fontWeight: '700'
                                }}>
                                  🛡️ Cardholder
                                </span>
                              )}
                              <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                                • {new Date(comment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              {comment.is_author && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteComment(comment.comment_id)}
                                  style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer', padding: '2px' }}
                                >
                                  <Trash2 size={12} />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => setReportTarget({ type: 'COMMENT', id: comment.comment_id })}
                                style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '2px' }}
                              >
                                <Flag size={12} />
                              </button>
                            </div>
                          </div>

                          <p style={{ margin: 0, fontSize: '12.5px', color: 'var(--text-main)', lineHeight: '1.4' }}>
                            {comment.content}
                          </p>

                          <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '2px' }}>
                            <button
                              type="button"
                              onClick={() => handleToggleLikeComment(comment.comment_id)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: comment.user_has_liked ? 'var(--color-outflow)' : 'var(--text-muted)',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '11px',
                                fontWeight: '600'
                              }}
                            >
                              <Heart size={12} fill={comment.user_has_liked ? 'var(--color-outflow)' : 'none'} />
                              <span>{comment.likes_count || 0}</span>
                            </button>
                          </div>
                        </div>
                      ))
                    )}

                    {/* Comment Composer */}
                    <form onSubmit={handleCreateComment} style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                      <input
                        type="text"
                        placeholder="Write a reply..."
                        value={commentContent}
                        onChange={e => setCommentContent(e.target.value)}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          borderRadius: '8px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: hasPaymentInfoInComment ? '1px solid var(--color-outflow)' : '1px solid var(--border-glass)',
                          color: 'var(--text-main)',
                          fontSize: '12px'
                        }}
                      />
                      <button
                        type="submit"
                        disabled={!commentContent.trim() || hasPaymentInfoInComment || isSubmittingComment}
                        className="btn-primary"
                        style={{
                          padding: '8px 14px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: '700',
                          opacity: (!commentContent.trim() || hasPaymentInfoInComment || isSubmittingComment) ? 0.5 : 1
                        }}
                      >
                        Reply
                      </button>
                    </form>
                    {hasPaymentInfoInComment && (
                      <span style={{ fontSize: '11px', color: 'var(--color-outflow)' }}>
                        ⚠️ Card numbers or CVVs are not allowed.
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* REPORT CONTENT MODAL */}
      {reportTarget && (
        <div className="modal-backdrop modal-backdrop-blur" style={{ zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <form
            onSubmit={handleSendReport}
            className="glass-panel modal-spring-enter"
            style={{
              width: '90%',
              maxWidth: '420px',
              padding: '20px',
              borderRadius: '16px',
              background: 'var(--bg-secondary, #161824)',
              border: '1px solid var(--border-glass)',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
              boxSizing: 'border-box'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '15px', color: 'var(--text-main)', fontWeight: '800' }}>
                Report {reportTarget.type === 'POST' ? 'Post' : 'Comment'}
              </h3>
              <button
                type="button"
                onClick={() => setReportTarget(null)}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
              >
                <X size={18} />
              </button>
            </div>

            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>
              Help keep our cardholder community safe and respectful. Content reported by multiple users will be auto-hidden pending admin moderation.
            </p>

            <div>
              <label style={{ fontSize: '12px', fontWeight: '700', display: 'block', marginBottom: '6px' }}>
                Reason for reporting
              </label>
              <select
                value={reportReason}
                onChange={e => setReportReason(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid var(--border-glass)',
                  color: 'var(--text-main)',
                  fontSize: '12px'
                }}
              >
                <option value="Spam or advertising">Spam or advertising</option>
                <option value="Payment card / sensitive information leak">Payment card / sensitive info leak</option>
                <option value="Harassment or inappropriate language">Harassment or inappropriate language</option>
                <option value="False or misleading financial terms">False or misleading terms</option>
                <option value="Other violation">Other violation</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
              <button
                type="button"
                onClick={() => setReportTarget(null)}
                className="btn-secondary"
                style={{ flex: 1, padding: '10px', borderRadius: '10px', fontSize: '12px' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmittingReport}
                className="btn-primary"
                style={{ flex: 1, padding: '10px', borderRadius: '10px', fontSize: '12px', fontWeight: '700', background: 'var(--color-outflow)' }}
              >
                {isSubmittingReport ? 'Submitting...' : 'Submit Report'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
